import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Alert,
  Button,
  ButtonWithSpinner,
  Card,
  CardTitle,
  Loading,
  ToastContext,
  TransactionDetailHeader,
  useFetch,
} from '@reveni-io/reveni-ui'
import { Divider } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import { websocketActionsWorkspaces } from '@/common/context/websocketActions/constants'
import useWebsocketActions from '@/common/hooks/useWebsocketActions/useWebsocketActions'
import ChargebackErrorModal from '@/returns/components/ChargebackErrorModal/ChargebackErrorModal'
import RefundMethodSelector from '@/returns/components/RefundMethodSelector/RefundMethodSelector'
import RefundNotes from '@/returns/components/RefundNotes/RefundNotes'
import RefundSummary from '@/returns/components/RefundSummary/RefundSummary'
import TotalRefundAmount from '@/returns/components/TotalRefundAmount/TotalRefundAmount'
import { getSumOfSelectedConcepts } from '@/returns/helpers/helpers'
import useChargebackErrorModal from '@/returns/hooks/useChargebackErrorModal/useChargebackErrorModal'
import useRefundMethodExtra from '@/returns/hooks/useRefundMethodExtra/useRefundMethodExtra'
import returnsServices from '@/returns/services/services'
import routes from '@/Router/routes.constants'

import { pendingWebsocketActionTypes } from '../TransactionDetailPage/hooks/useWebsocketTransactionAction/constants'
import useManualIssueRefund from './hooks/useManualIssueRefund'
import IssueRefundErrorModal from './IssueRefundErrorModal/IssueRefundErrorModal'
import RefundConcepts from './RefundConcepts/RefundConcepts'
import { getRefundMethodExtraLabel, shouldShowRefundMethodExtra } from './helpers'
import * as Styles from './styles'

const TransactionIssueRefundPage = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { info } = useContext(AppContext)
  const { data: transaction, loading } = useFetch(
    () => returnsServices.getReturnDetail({ returnId: id, storeId: info?.selectedStore?.id }),
    {
      doInitialRequest: true,
    }
  )
  const {
    refundMethod,
    setRefundMethod,
    note,
    setNote,
    concepts,
    onClickInConcept,
    onChangeConceptAmount,
    onChangeItemAmount,
    isFormValid,
    isIssueRefundLoading,
    onIssueRefund,
    error,
    availableRefundMethods,
    getSelectedConcepts,
    hasConceptAmountErrors,
  } = useManualIssueRefund({
    refundType: transaction?.refund_info?.available_refund_type,
    adjustMentRefundMethods: transaction?.refund_info?.adjustment_refund_methods,
    adjustmentConcepts: transaction?.refund_info?.adjustment_concepts,
    refundMethods: transaction?.refund_info?.refund_methods,
    refundConcepts: transaction?.refund_info?.refund_concepts,
    lineItems: transaction?.line_items,
    lineItemGroups: transaction?.line_item_groups,
    returnId: id,
  })
  const { setToastInfo } = useContext(ToastContext)
  const { setPendingWebsocketAction } = useWebsocketActions(websocketActionsWorkspaces.transactions)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const { showChargebackModal, closeChargebackModal, handleChargebackError } = useChargebackErrorModal()

  const onCancel = () => {
    navigate(routes.transactionDetailPage.replace(':id', id))
  }

  const closeErrorModal = () => {
    setShowErrorModal(false)
  }

  const { amount: refundMethodExtraAmount } = useRefundMethodExtra({
    returnId: id,
    amount: getSumOfSelectedConcepts(getSelectedConcepts()),
    refundMethod,
    skip: hasConceptAmountErrors,
  })

  const showExtra = shouldShowRefundMethodExtra(
    transaction?.refund_info?.available_refund_type,
    refundMethod,
    refundMethodExtraAmount
  )

  const onAccept = async () => {
    try {
      const response = await onIssueRefund()

      setToastInfo({ show: true, message: t('Reembolso emitido correctamente'), type: 'success' })
      setPendingWebsocketAction(pendingWebsocketActionTypes.issueRefund, response)
      navigate(routes.transactionDetailPage.replace(':id', id))
    } catch (err) {
      if (handleChargebackError(err)) return

      setShowErrorModal(true)
    }
  }

  return (
    <Styles.Container>
      <Loading show={loading}>
        <TransactionDetailHeader
          showArrowsNavigation={false}
          orderNumber={`${t('Reembolso')} ${transaction?.order?.name}`}
          isInstant={transaction?.is_instant}
          transactionType={transaction?.transaction_type}
          backListButtonText={t('Volver al detalle')}
        />
        <Styles.MainContainer>
          <Card showBorder={false}>
            <CardTitle>{t('Conceptos')}</CardTitle>
            <Styles.RefundConceptsContainer>
              <RefundConcepts
                concepts={concepts}
                currency={transaction?.summary?.currency}
                editable
                onClickInConcept={onClickInConcept}
                onChangeConceptAmount={onChangeConceptAmount}
                onChangeItemAmount={onChangeItemAmount}
                lineItems={transaction?.line_items}
                newItems={transaction?.new_items}
              />
            </Styles.RefundConceptsContainer>
          </Card>
          <Card showBorder={false}>
            <CardTitle>{t('Resumen')}</CardTitle>
            <Styles.SummaryContainer>
              <RefundSummary
                currency={transaction?.summary?.currency}
                selectedConcepts={getSelectedConcepts()}
                extraAmount={refundMethodExtraAmount}
                extraLabel={getRefundMethodExtraLabel(refundMethod, t)}
                showExtra={showExtra}
              />
              <TotalRefundAmount
                currency={transaction?.summary?.currency}
                selectedConcepts={getSelectedConcepts()}
                extraAmount={refundMethodExtraAmount}
                showExtra={showExtra}
                hasError={hasConceptAmountErrors}
              />
              {hasConceptAmountErrors && (
                <Alert type="error">{t('Corrige los errores de importe para continuar')}</Alert>
              )}
              <RefundMethodSelector
                refundMethods={availableRefundMethods}
                refundMethodSelected={refundMethod}
                onChange={setRefundMethod}
              />
              <Divider />
              <RefundNotes value={note} onChange={setNote} />
              <Styles.ActionsContainer>
                <ButtonWithSpinner
                  fullWidth
                  defaultLabel={t('Emitir reembolso')}
                  disabled={!isFormValid() || isIssueRefundLoading}
                  loading={isIssueRefundLoading}
                  onClick={onAccept}
                  loadingLabel={t('Emitir reembolso')}
                />
                <Button fullWidth variant="outlined" onClick={onCancel}>
                  {t('Cancelar')}
                </Button>
              </Styles.ActionsContainer>
            </Styles.SummaryContainer>
          </Card>
        </Styles.MainContainer>
      </Loading>
      <IssueRefundErrorModal show={showErrorModal} onClose={closeErrorModal} error={error?.response?.data} />
      <ChargebackErrorModal show={showChargebackModal} onClose={closeChargebackModal} />
    </Styles.Container>
  )
}

export default TransactionIssueRefundPage
